import { useState } from "react";
import jsPDF, { HTMLOptions } from "jspdf";
import { API_ROUTES } from "@/services/environment";
import { GET, POST } from "@/services/api";
import { useAppSelector } from "./hook";

interface UseDownloadPdfResult {
    loading: boolean;
    downloadPdf: (transactionId: string) => Promise<string>;
    downloadStorePdf: (transactionId: string) => Promise<string>;
    downloadMultiPagePdf: (transactionId: string) => Promise<string>;
    downloadQuotePdf: (transactionId: string) => Promise<string>;
}

export const useDownloadPdf = (): UseDownloadPdfResult => {
    const { refreshToken } = useAppSelector((store) => store.user);
    const { downloadPdfInvoice, downloadStorePdfInvoice } = API_ROUTES;
    const [loading, setLoading] = useState(false);
    const downloadMultiPagePdf = async (
        transactionId: string
    ): Promise<string> => {
        setLoading(true);
        console.log("useDownloadPdfTwoPage hook initialized");
        try {
            const response = await GET(
                `${downloadPdfInvoice}/${transactionId}`,
                refreshToken
            );
            const { page1: page2, page2: page1 } = response.data;
            console.log("Page 1 Content:", page1);
            console.log("Page 2 Content:", page2);

            // Create a wrapper container
            const container = document.createElement("div");
            container.style.width = "800px";
            container.style.padding = "20px";
            container.style.boxSizing = "border-box";
            container.style.background = "white";
            container.style.color = "black";

            // Inject scoped style to handle forced page break
            const style = document.createElement("style");
            style.textContent = `
        .pdf-page {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .pdf-break {
          page-break-before: always;
          break-before: page;
          height: 800px;
        }
      `;
            container.appendChild(style);

            // Add page1 and page2 content with a forced break
            const page1Wrapper = document.createElement("div");
            page1Wrapper.className = "pdf-page";
            page1Wrapper.innerHTML = page1;

            const breakWrapper = document.createElement("div");
            breakWrapper.className = "pdf-break";

            const page2Wrapper = document.createElement("div");
            page2Wrapper.className = "pdf-page";
            page2Wrapper.innerHTML = page2;

            container.appendChild(page1Wrapper);
            if (page2) {
                container.appendChild(breakWrapper);
                container.appendChild(page2Wrapper);
            }

            // Append to body temporarily so styles are computed
            document.body.appendChild(container);

            const pdf = new jsPDF("p", "mm", "a4");

            await pdf.html(container, {
                x: 6,
                y: 10,
                width: 190,
                scale: 2,
                windowWidth: 800,
                autoPaging: true,
            } as HTMLOptions);

            document.body.removeChild(container); // Clean up

            const fileName = `invoice_${transactionId}.pdf`;
            pdf.save(fileName);

            return fileName;
        } catch (error) {
            console.log("Error downloading the PDF:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };
    const downloadPdf = async (transactionId: string): Promise<string> => {
        setLoading(true);
        try {
            const response = await GET(
                `${downloadPdfInvoice}/${transactionId}`,
                refreshToken
            );
            const htmlContent = response.data.page1;
            console.log("HTML Content:", response);
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, "text/html");
            const fullHtmlContent = doc.documentElement.outerHTML;

            const pdf = new jsPDF("p", "mm", "a4");
            pdf.setFont("courier", "italic");
            await pdf.html(fullHtmlContent, {
                x: 6, // Left margin
                y: 10, // Top margin
                width: 190, // Fit content to A4 width (210mm - 20mm margin)
                scale: 2,
                windowWidth: 800, // Optional: Render width of the HTML content
            } as HTMLOptions);

            const fileName = `invoice_${transactionId}.pdf`;
            pdf.save(fileName);

            return fileName;
        } catch (error) {
            console.log("Error downloading the PDF:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };
    const downloadStorePdf = async (transactionId: string): Promise<string> => {
        setLoading(true);
        try {
            const response = await GET(
                `${downloadStorePdfInvoice}/${transactionId}`,
                refreshToken
            );
            const htmlContent = response.data;
            console.log("HTML Content:", response);
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, "text/html");
            const fullHtmlContent = doc.documentElement.outerHTML;

            const pdf = new jsPDF("p", "mm", "a4");
            pdf.setFont("courier", "italic");
            await pdf.html(fullHtmlContent, {
                x: 6, // Left margin
                y: 10, // Top margin
                width: 190, // Fit content to A4 width (210mm - 20mm margin)
                scale: 2,
                windowWidth: 800, // Optional: Render width of the HTML content
            } as HTMLOptions);

            const fileName = `invoice_${transactionId}.pdf`;
            pdf.save(fileName);

            return fileName;
        } catch (error) {
            console.log("Error downloading the PDF:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const downloadQuotePdf = async (quoteId: string): Promise<string> => {
        setLoading(true);
        try {
            const response = await POST(`quotes/pdf/${quoteId}`, refreshToken);
            const {
                eventDetailsPdf: page1,
                packageDetailsPdf: page2,
                alcoholDetailsPdf: page3,
                paymentSummaryPdf: page4,
            } = response.data.data;

            const pdfRoot = document.getElementById("pdf") as HTMLDivElement;

            // Create a wrapper container
            const container = document.createElement("div");
            container.style.width = "800px";
            container.style.padding = "20px";
            container.style.boxSizing = "border-box";
            container.style.background = "white";
            container.style.color = "black";

            // Inject scoped style to handle forced page break
            const style = document.createElement("style");
            container.appendChild(style);

            // Add page1 and page2 content with a forced break
            const page1Wrapper = document.createElement("div");
            page1Wrapper.style.height = "1250px";
            page1Wrapper.className = "pdf-page";
            page1Wrapper.innerHTML = page1;

            const breakWrapper = document.createElement("div");
            breakWrapper.className = "pdf-break";

            const page2Wrapper = document.createElement("div");
            page2Wrapper.className = "pdf-page";
            page2Wrapper.style.height = "1250px";
            page2Wrapper.innerHTML = page2;
            container.appendChild(page1Wrapper);
            if (page2) {
                container.appendChild(breakWrapper.cloneNode());
                container.appendChild(page2Wrapper);
            }
            if (page3) {
                container.appendChild(breakWrapper.cloneNode());
                const page3Wrapper = document.createElement("div");
                page3Wrapper.style.height = "1250px";
                page3Wrapper.className = "pdf-page";
                page3Wrapper.innerHTML = page3;
                container.appendChild(page3Wrapper);
            }
            if (page4) {
                container.appendChild(breakWrapper.cloneNode());
                const page4Wrapper = document.createElement("div");
                page4Wrapper.className = "pdf-page";
                page4Wrapper.innerHTML = page4;
                container.appendChild(page4Wrapper);
            }

            pdfRoot.style.display = "block";
            pdfRoot.style.visibility = "hidden";
            pdfRoot.appendChild(container);

            const pdf = new jsPDF("p", "mm", "a4");

            await pdf.html(container, {
                x: 6,
                y: 10,
                width: 190,
                scale: 2,
                windowWidth: 800,
                autoPaging: true,
            } as HTMLOptions);

            // Add clickable link annotations (pdf.html renders as bitmap, losing href)
            const anchors = container.querySelectorAll<HTMLAnchorElement>('a[href]');
            const containerRect = container.getBoundingClientRect();
            const mmPerPx = 190 / 800;

            anchors.forEach((anchor) => {
                const rect = anchor.getBoundingClientRect();
                const relXPx = rect.left - containerRect.left;
                const relYPx = rect.top - containerRect.top;

                const xMm = 6 + relXPx * mmPerPx;
                const totalYMm = 10 + relYPx * mmPerPx;
                const widthMm = rect.width * mmPerPx;
                const heightMm = Math.max(rect.height * mmPerPx, 5);

                const pageIndex = Math.floor(totalYMm / 297);
                const yOnPageMm = totalYMm - pageIndex * 297;

                pdf.setPage(pageIndex + 1);
                pdf.link(xMm, yOnPageMm, widthMm, heightMm, { url: anchor.href });
            });

            pdfRoot.innerHTML = "";
            pdfRoot.style.visibility = "";
            pdfRoot.style.display = "none";

            const fileName = `quote_${quoteId}.pdf`;
            pdf.save(fileName);

            return fileName;
        } catch (error) {
            console.log("Error downloading the PDF:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        downloadPdf,
        downloadMultiPagePdf,
        downloadStorePdf,
        downloadQuotePdf,
    };
};

export default useDownloadPdf;
